import React from 'react';
import AITemplateGenerator from './ai/AITemplateGenerator';
import { GeneratedTemplate, PromptContext } from './ai/promptTemplates';

export interface VoiceTemplateGeneratorProps {
  onTemplateGenerated: (template: any) => void;
  onCancel?: () => void;
  isGenerating: boolean;
  generationError: string | null;
  onClearError: () => void;
  className?: string;
}

const VoiceTemplateGenerator: React.FC<VoiceTemplateGeneratorProps> = ({
  onTemplateGenerated,
  onCancel,
  isGenerating,
  generationError,
  onClearError,
  className = ''
}) => {
  // Convert new format to existing format for compatibility
  const handleTemplateGenerated = (template: GeneratedTemplate) => {
    // Transform to match existing interface
    const compatibleTemplate = {
      name: template.name,
      description: template.description,
      voicePrompt: template.voicePrompt || '',
      elements: template.elements.map(element => ({
        ...element,
        // Ensure compatibility with existing element structure
        id: element.id || `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      })),
      category: template.category,
      metadata: template.metadata
    };

    onTemplateGenerated(compatibleTemplate);
  };

  // Handle cancel/close
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Use enhanced AI generator if not currently generating
  if (!isGenerating) {
    return (
      <AITemplateGenerator
        onTemplateGenerated={handleTemplateGenerated}
        onCancel={handleCancel}
        className={className}
      />
    );
  }

  // Show legacy loading state if needed
  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Generating your template...</p>
        <p className="text-sm text-gray-500 mt-2">Please wait while AI creates your template</p>
      </div>

      {generationError && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-red-700">
            <span>⚠️</span>
            <span className="text-sm font-medium">Generation Error</span>
          </div>
          <p className="text-sm text-red-600 mt-1">{generationError}</p>
          <button
            onClick={onClearError}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

export default VoiceTemplateGenerator; 